import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { rateTables2026, type RateTables } from '@wolhasudang/engine';
import { publicServantRateSchema, rateTableKinds, rateTablesSchema, remoteRateTableResponseSchema, sourceRefSchema } from '@wolhasudang/shared';
import { z } from 'zod';
import { db } from './db.js';
import { platform } from './platform.js';

const publicRatesPayloadSchema = z.object({
  source: sourceRefSchema,
  rates: z.record(z.string(), publicServantRateSchema),
}).strict();

const cachedSchema = z.object({
  tables: rateTablesSchema,
  verifiedAt: z.number().int().positive(),
}).strict();

interface RemoteRateTableResponse {
  year: number;
  schemaVersion: 1;
  data: Record<string, unknown>;
  updatedAt: number | string;
  sourceRefs: Array<{
    kind: 'minWage' | 'publicServantRates' | 'holidays' | 'fourInsurance' | 'privateRulePolicy';
    url: string; checkedAt: number; version: string; checksum: string; effectiveFrom: string; effectiveTo: string | null;
  }>;
  fallback: boolean;
}

export type RateTableState = {
  tables: RateTables;
  status: 'builtin' | 'remote' | 'cached' | 'stale_cache' | 'invalid_remote';
  checkedAt: number;
};

const builtinState: RateTableState = {
  tables: rateTables2026,
  status: 'builtin',
  checkedAt: Date.parse(rateTables2026.privateRulePolicy.source.checkedAt),
};

export async function resolveRemoteRateTables(payload: unknown, now = new Date()): Promise<RateTableState> {
  const parsed = remoteRateTableResponseSchema.safeParse(payload);
  if (!parsed.success) return { ...builtinState, status: 'invalid_remote', checkedAt: now.getTime() };
  const remote = parsed.data as RemoteRateTableResponse;
  if (remote.year !== 2026) return { ...builtinState, status: 'invalid_remote', checkedAt: now.getTime() };
  if (remote.fallback) {
    const fallback = rateTablesSchema.safeParse(remote.data);
    return fallback.success
      ? { tables: fallback.data as RateTables, status: 'builtin', checkedAt: now.getTime() }
      : { ...builtinState, status: 'invalid_remote', checkedAt: now.getTime() };
  }

  const dataKinds = Object.keys(remote.data);
  const referencedKinds = remote.sourceRefs.map((source) => source.kind);
  if (!referencedKinds.length || new Set(referencedKinds).size !== referencedKinds.length
    || dataKinds.length !== referencedKinds.length
    || dataKinds.some((kind) => !rateTableKinds.includes(kind as typeof rateTableKinds[number]) || !referencedKinds.includes(kind as typeof referencedKinds[number]))) {
    return { ...builtinState, status: 'invalid_remote', checkedAt: now.getTime() };
  }

  const currentDate = now.toISOString().slice(0, 10);
  for (const source of remote.sourceRefs) {
    const value = remote.data[source.kind];
    if (value === undefined || source.effectiveFrom > currentDate || (source.effectiveTo && source.effectiveTo < currentDate)) {
      return { ...builtinState, status: 'invalid_remote', checkedAt: now.getTime() };
    }
    if (await sha256Hex(canonicalJson(value)) !== source.checksum) {
      return { ...builtinState, status: 'invalid_remote', checkedAt: now.getTime() };
    }
  }

  const privatePolicy = remote.data.privateRulePolicy ?? rateTables2026.privateRulePolicy;
  const publicResult = remote.data.publicServantRates === undefined
    ? undefined
    : publicRatesPayloadSchema.safeParse(remote.data.publicServantRates);
  const publicPayload = remote.data.publicServantRates === undefined
    ? { source: rateTables2026.publicServantSource, rates: rateTables2026.publicServantRates }
    : publicResult?.success ? publicResult.data : undefined;
  if (!publicPayload) return { ...builtinState, status: 'invalid_remote', checkedAt: now.getTime() };
  if (remote.data.holidays !== undefined) return { ...builtinState, status: 'invalid_remote', checkedAt: now.getTime() };
  const versions = { ...rateTables2026.versions };
  const provenance = { ...rateTables2026.provenance };
  for (const source of remote.sourceRefs) {
    versions[source.kind] = source.version;
    if (source.kind in provenance) {
      provenance[source.kind as keyof typeof provenance] = {
        version: source.version,
        effectiveFrom: source.effectiveFrom,
        effectiveTo: source.effectiveTo,
        sourceUrl: source.url,
        checkedAt: new Date(source.checkedAt).toISOString(),
        checksum: source.checksum,
      };
    }
  }
  const candidate = rateTablesSchema.safeParse({
    schemaVersion: 1,
    versions,
    provenance,
    privateRulePolicy: privatePolicy,
    publicServantSource: publicPayload.source,
    publicServantRates: publicPayload.rates,
    taxRates: {
      ...rateTables2026.taxRates,
      fourInsurance: remote.data.fourInsurance ?? rateTables2026.taxRates.fourInsurance,
    },
    minimumWage: remote.data.minWage ?? rateTables2026.minimumWage,
  });
  return candidate.success
    ? { tables: candidate.data as RateTables, status: 'remote', checkedAt: now.getTime() }
    : { ...builtinState, status: 'invalid_remote', checkedAt: now.getTime() };
}

export async function loadRateTables(fetcher: typeof fetch = fetch): Promise<RateTableState> {
  const cached = cachedSchema.safeParse((await db.settings.get('rateTables:2026'))?.value);
  const cachedState = cached.success ? {
    tables: cached.data.tables as RateTables,
    status: (Date.now() - cached.data.verifiedAt > 7 * 86_400_000 ? 'stale_cache' : 'cached') as 'cached' | 'stale_cache',
    checkedAt: cached.data.verifiedAt,
  } : undefined;
  try {
    const response = await fetcher(platform.apiUrl('/api/v1/tables/2026'), { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('RATE_TABLE_FETCH_FAILED');
    const resolved = await resolveRemoteRateTables(await response.json());
    if (resolved.status === 'remote') await db.settings.put({ key: 'rateTables:2026', value: { tables: resolved.tables, verifiedAt: resolved.checkedAt } });
    return resolved.status === 'invalid_remote' && cachedState
      ? cachedState
      : resolved;
  } catch {
    return cachedState
      ? cachedState
      : builtinState;
  }
}

const RateTablesContext = createContext<RateTableState>(builtinState);

export function RateTablesProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<RateTableState>(builtinState);
  useEffect(() => {
    let active = true;
    if (navigator.onLine) void loadRateTables().then((next) => { if (active) setState(next); });
    return () => { active = false; };
  }, []);
  const value = useMemo(() => state, [state]);
  return <RateTablesContext.Provider value={value}>{children}</RateTablesContext.Provider>;
}

export function useRateTables(): RateTableState {
  return useContext(RateTablesContext);
}

function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  if (value && typeof value === 'object') return `{${Object.entries(value as Record<string, unknown>).sort(([left], [right]) => left.localeCompare(right)).map(([key, entry]) => `${JSON.stringify(key)}:${canonicalJson(entry)}`).join(',')}}`;
  return JSON.stringify(value) ?? 'null';
}

async function sha256Hex(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}
